/**
 * Phase 8 · Addendum F §33.1 — Sourcing thread helpers.
 *
 * Spec:  docs/ux/PHASE8_WRANGLE_TRANSFORMATION.md §33 (Wrangle Sourcing).
 * Smoke: docs/ux/PHASE8_VISUAL_TRACKER.md §8.F (persistent history column).
 *
 * Sourcing threads share the same localStorage store as Smart Match
 * (`ggj.smartmatch.threads`) because:
 *
 *   1. The Wrangle UX always starts in the same 3-column canvas
 *      (`MatchCanvasLayout`). Keeping one store means a user can promote
 *      a Sourcing conversation to a full Smart Match match-run without
 *      a copy.
 *   2. The rename/delete/duplicate semantics are identical.
 *   3. The export path (CSV, §33.8) is the same shape.
 *
 * The only delta is the `origin` tag on the row — this module owns the
 * filter so the Sourcing history column never shows Smart Match threads
 * (and vice-versa).
 */

import {
  readThreads,
  upsertThread,
  patchThread,
  renameThread,
  deleteThread,
  threadNameFromPrompt,
  type SmartMatchThread,
  type SmartMatchOrigin,
} from '@modules/smart-match/lib/smartMatchThreads';

export type SourcingThread = SmartMatchThread & { origin?: 'sourcing' };

export const SOURCING_ORIGIN: SmartMatchOrigin = 'sourcing';

export function readSourcingThreads(): SourcingThread[] {
  return readThreads().filter((t) => t.origin === 'sourcing') as SourcingThread[];
}

export function getSourcingThread(id: string): SourcingThread | null {
  const t = readThreads().find((x) => x.id === id);
  if (!t) return null;
  if (t.origin !== 'sourcing') return null;
  return t as SourcingThread;
}

/** Factory — creates a new sourcing thread from a user prompt + scope id. */
export function createSourcingThread(input: {
  id: string;
  prompt: string;
  scopeId: string;
  createdByUserId?: string;
}): SourcingThread {
  const now = Date.now();
  const thread: SourcingThread = {
    id: input.id,
    ...(input.createdByUserId ? { createdByUserId: input.createdByUserId } : {}),
    name: threadNameFromPrompt(input.prompt),
    prompt: input.prompt,
    intakeTab: 'job-description',
    scopeId: input.scopeId,
    collection: null,
    candidateCount: 0,
    createdAt: now,
    updatedAt: now,
    stage: 'clarify',
    clarificationRoundCount: 0,
    mode: 'single',
    origin: 'sourcing',
  };
  upsertThread(thread);
  return thread;
}

// Re-export the mutation helpers verbatim so surfaces in `modules/sourcing`
// never need to cross-import smart-match internals.
export { patchThread, renameThread, deleteThread };
